import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { emergencyKeywords, intentPhotos, intents, profileAddress } from '../data/demandes'
import { useRequests } from '../contexts/RequestsContext'
import type { AtnaTurn, AtnaTurnKind, ConversationTurn, FollowUpInfo, Intent } from '../types/demandes'

/** Requête de démo suivie par ATNA quand la citoyenne reparle du lampadaire. */
const FOLLOW_UP_REQUEST_ID = 'req-2790'

function mentionsFollowUp(text: string): boolean {
  return text.toLowerCase().includes('lampadaire')
}

function matchIntent(text: string): Intent | undefined {
  const lower = text.toLowerCase()
  return intents.find((intent) => intent.keywords.some((keyword) => lower.includes(keyword)))
}

function detect(text: string): Intent {
  return matchIntent(text) ?? intents[0]
}

function isEmergency(text: string): boolean {
  const lower = text.toLowerCase()
  return emergencyKeywords.some((keyword) => lower.includes(keyword))
}

const THINKING_MS = 550

/**
 * Drives the ATNA request conversation: the citizen's messages, ATNA's replies
 * and the small state machine (intent → lieu → photo → précision → revue) behind them.
 * Seeded with the description typed in the launcher, so the thread already has content
 * the moment the dialog opens.
 */
export function useAtnaConversation(initialText: string, direct = false, presetAddress?: string) {
  const seed = initialText.trim()
  const defaultAddress = presetAddress ?? profileAddress

  const [turns, setTurns] = useState<ConversationTurn[]>(() => [{ id: 't0', role: 'user', text: seed }])
  const [thinking, setThinking] = useState(true)
  const [draft, setDraft] = useState('')
  const [description, setDescription] = useState(seed)
  const [intent, setIntent] = useState<Intent>(() => detect(seed))
  const [address, setAddress] = useState(defaultAddress)
  const [hasPhoto, setHasPhoto] = useState(false)
  const [answer, setAnswer] = useState<string | null>(null)

  // Suivi : la requête du lampadaire est encore ouverte. La question est figée à l'ouverture
  // pour que l'historique de la conversation reste lisible une fois la réponse envoyée.
  const { requests, replyToRequest } = useRequests()
  const [followUp] = useState<FollowUpInfo | null>(() => {
    const request = requests.find((item) => item.id === FOLLOW_UP_REQUEST_ID && item.status !== 'resolue')
    if (!request) return null
    const pending = [...request.messages].reverse().find((message) => message.author === 'ville' && message.awaiting)
    return {
      id: request.id,
      reference: request.reference,
      title: request.title,
      address: request.address,
      question: pending?.body ?? null,
    }
  })
  const [followUpAnswered, setFollowUpAnswered] = useState(false)
  const isFollowUp = (text: string) => Boolean(followUp) && mentionsFollowUp(text)

  const idRef = useRef(1)
  const timerRef = useRef<number | null>(null)

  const reply = useCallback((kind: AtnaTurnKind) => {
    if (timerRef.current) window.clearTimeout(timerRef.current)
    setThinking(true)
    timerRef.current = window.setTimeout(() => {
      idRef.current += 1
      setTurns((current) => [...current, { id: `t${idRef.current}`, role: 'atna', kind }])
      setThinking(false)
    }, THINKING_MS)
  }, [])

  /** Appends the citizen's message, then ATNA's reply after a short beat. */
  const exchange = useCallback(
    (userText: string, kind: AtnaTurnKind, withPhoto = false, photoUrl?: string) => {
      idRef.current += 1
      const id = `t${idRef.current}`
      setTurns((current) => [...current, { id, role: 'user', text: userText, withPhoto, photoUrl }])
      reply(kind)
    },
    [reply],
  )

  useEffect(() => {
    // Un raccourci explicite (ex. « Nid-de-poule ») saute la reformulation et demande tout de suite le lieu.
    // Ouvert depuis la carte : le lieu est connu, ATNA demande quel est le problème.
    // Elle reparle du lampadaire déjà signalé : c'est un suivi, pas un nouveau signalement.
    const firstStep: AtnaTurnKind = presetAddress
      ? 'quoi'
      : isFollowUp(seed) && !isEmergency(seed)
        ? 'suivi'
        : isEmergency(seed)
        ? 'urgence'
        : direct && matchIntent(seed)
          ? 'lieu'
          : 'comprehension'
    reply(firstStep)
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current)
    }
    // Seeded once when the conversation opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const lastAtna = useMemo(
    () => [...turns].reverse().find((turn): turn is AtnaTurn => turn.role === 'atna'),
    [turns],
  )

  const restart = useCallback(
    (text: string) => {
      setDescription(text)
      setIntent(detect(text))
      setAnswer(null)
      setAddress(defaultAddress)
      const followUpTurn = !followUpAnswered && isFollowUp(text) && !isEmergency(text)
      exchange(text, isEmergency(text) ? 'urgence' : followUpTurn ? 'suivi' : 'comprehension')
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [exchange, defaultAddress, followUpAnswered],
  )

  /** Réponse à la précision demandée : poussée dans les messages de la requête. */
  const answerFollowUp = useCallback(
    (text: string) => {
      if (!followUp) return
      replyToRequest(followUp.id, text)
      setFollowUpAnswered(true)
      exchange(text, 'suivi-merci')
    },
    [exchange, followUp, replyToRequest],
  )

  /** « C'est un autre problème » : on repart sur un nouveau signalement de lampadaire. */
  const startNewReport = useCallback(() => {
    setIntent(detect(description))
    exchange('C’est un autre problème', 'comprehension')
  }, [description, exchange])

  /** Choix rapide du problème quand le lieu vient de la carte : on passe directement à la photo. */
  const pickProblem = useCallback(
    (label: string) => {
      setDescription(label)
      setIntent(detect(label))
      setAnswer(null)
      exchange(label, hasPhoto ? 'detail' : 'photo')
    },
    [exchange, hasPhoto],
  )

  /** « Signaler autre chose » : nouveau signalement dans la même conversation. */
  const startOver = useCallback(() => {
    setAnswer(null)
    setHasPhoto(false)
    setAddress(defaultAddress)
    exchange('Signaler autre chose', 'aide')
  }, [defaultAddress, exchange])

  /** Raccourci proposé après « Comment puis-je vous aider ? » : on demande directement le lieu. */
  const pickHelpTopic = useCallback(
    (label: string) => {
      setDescription(label)
      setIntent(detect(label))
      exchange(label, 'lieu')
    },
    [exchange],
  )

  /** Free-text answers advance whatever question ATNA asked last. */
  const submitDraft = useCallback(() => {
    const text = draft.trim()
    if (!text || thinking) return
    setDraft('')

    switch (lastAtna?.kind) {
      case 'lieu':
        setAddress(text)
        exchange(text, hasPhoto ? 'detail' : 'photo')
        break
      case 'photo':
        exchange(text, 'detail')
        break
      case 'detail':
        setAnswer(text)
        exchange(text, 'revue')
        break
      case 'quoi':
        // Lieu déjà connu (carte) et problème reconnu : pas de « J'ai compris », on passe à la photo.
        if (!isEmergency(text) && matchIntent(text)) {
          pickProblem(text)
          break
        }
        restart(text)
        break
      case 'suivi':
        if (followUp?.question && !followUpAnswered) {
          answerFollowUp(text)
          break
        }
        restart(text)
        break
      case 'urgence':
      case 'comprehension':
      default:
        restart(text)
    }
  }, [answerFollowUp, draft, exchange, followUp, followUpAnswered, hasPhoto, lastAtna, pickProblem, restart, thinking])

  const attachPhoto = useCallback(() => {
    setHasPhoto(true)
    if (lastAtna?.kind !== 'photo') return
    // Téléversement simulé : pour un nid-de-poule, la bulle montre la photo au lieu d'un texte.
    const photoUrl = intentPhotos[intent.label]
    if (photoUrl) exchange('Photo jointe', 'detail', true, photoUrl)
    else exchange('J’ai ajouté une photo', 'detail', true)
  }, [exchange, intent, lastAtna])

  return {
    turns,
    thinking,
    draft,
    setDraft,
    submitDraft,
    description,
    intent,
    setIntent,
    address,
    setAddress,
    answer,
    setAnswer,
    hasPhoto,
    attachPhoto,
    exchange,
    detect,
    pickProblem,
    startOver,
    pickHelpTopic,
    followUp,
    startNewReport,
    hasPresetAddress: Boolean(presetAddress),
    liveAtnaId: lastAtna?.id,
    lastAtnaKind: lastAtna?.kind,
  }
}
